import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { localeAtom } from '../../../features/Locale/localeStore';
import { Host } from '../../../features/Host/types/host';
import { hostService } from '../../../features/Host/hostService';
import Button from '../../../shared/Button';
import styles from './HostBanner.module.css';
import TextInput from '../../../shared/TextInput';

const ONLINE_THRESHOLD_MS = 15000;
const RELATIVE_DAYS_LIMIT = 10;

type Props = {
	host: Host;
};

const pluralise = (value: number, unit: string) => `${value} ${unit}${value === 1 ? "" : "s"}`;

const formatLastSeen = (lastSeen: Date | undefined, locale: string | undefined) =>
{
	if (!lastSeen)
	{
		return "Never connected";
	}

	const elapsedMinutes = Math.floor((Date.now() - lastSeen.getTime()) / 60000);

	if (elapsedMinutes < 1)
	{
		return "Last seen just now";
	}

	if (elapsedMinutes < 60)
	{
		return `Last seen ${pluralise(elapsedMinutes, "minute")} ago`;
	}

	const elapsedHours = Math.floor(elapsedMinutes / 60);

	if (elapsedHours < 24)
	{
		return `Last seen ${pluralise(elapsedHours, "hour")} ago`;
	}

	const elapsedDays = Math.floor(elapsedHours / 24);

	if (elapsedDays <= RELATIVE_DAYS_LIMIT)
	{
		return `Last seen ${pluralise(elapsedDays, "day")} ago`;
	}

	return `Last seen ${lastSeen.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" })}`;
};

const HostBanner = ({ host }: Props) =>
{
	const locale = useAtomValue(localeAtom);
	const [isConfirmingRemoval, setIsConfirmingRemoval] = useState<boolean>(false);
	const [isRemoving, setIsRemoving] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const isOnline = host.lastSeen !== undefined && Date.now() - host.lastSeen.getTime() < ONLINE_THRESHOLD_MS;

	const handleRemove = () =>
	{
		setIsRemoving(true);
		setError(null);

		hostService.remove(host.id)
			.catch((removeError: unknown) =>
			{
				setError(removeError instanceof Error ? removeError.message : "Failed to remove the server host");
				setIsRemoving(false);
				setIsConfirmingRemoval(false);
			});
	};

	const handleNameChange = async (newName: string) =>
	{
		const name = newName.trim();
		if (!name || name === host.name) return;

		setError(null);
		try
		{
			await hostService.updateName(host.id, name);
		}
		catch (updateError: unknown)
		{
			setError(updateError instanceof Error ? updateError.message : "Failed to update the server host name");
		}
	};

	return (
		<div className={styles.hostBanner}>
			<div className={styles.information}>
				<TextInput defaultValue={host.name} placeholder="Host name" onBlur={handleNameChange} />
				<div className={styles.metaData}>
					<div className={isOnline ? styles.online : styles.offline}>{isOnline ? "Online" : "Offline"}</div>
					{!isOnline && <div>{formatLastSeen(host.lastSeen, locale)}</div>}
					{isOnline && host.currentActivity === "syncing_scenarios" && <span>Syncing scenarios</span>}
				</div>
				{error && <div className={styles.error} role="alert">{error}</div>}
			</div>
			{isConfirmingRemoval
				? <div className={styles.confirmation}>
					<div className={styles.confirmationText}>Remove this host? The agent will ask to be linked again.</div>
					<Button label="Cancel" compact frameless disabled={isRemoving} onClick={() => setIsConfirmingRemoval(false)} />
					<Button label={isRemoving ? "Removing..." : "Remove"} compact disabled={isRemoving} onClick={handleRemove} />
				</div>
				: <Button label="Remove" compact onClick={() => setIsConfirmingRemoval(true)} />}
		</div>
	);
};

export default HostBanner;
